import Group, { ComposableElement } from "./Group";
import Space from "./Space";
import { ClusterElement } from './Cluster'
import Base from './Base'
import DomainFactory from "../lib/DomainFactory"
import Drivers from '../data/Drivers'
import { baseAttribute } from "./composition";
import Toggles from "@domain/toggles/Toggles";
import Light from "./Light";
import CompositionFactory from "@domain/factory/CompositionFactory";

export interface CompositionElement extends ClusterElement { }

const remoteInstallation = "3"

export class Composition extends Group {
    compositionNumber;
    type = "Composition";
    static className = "Composition";

    constructor(elements: CompositionElement[] | Group, parent?: Space, virtual?, attributes?, compositionNumber?: Number, dataComposition?: any) {
        super(elements, parent, virtual, null, attributes, dataComposition)
        this.compositionNumber = compositionNumber ? compositionNumber : 1
        this.reload()
    }

    reload() {
        let _elements = this.getElements() as CompositionElement[]
        /* Pone como padre del elemento agrupado (cluster) la composition */
        _elements.forEach(element => element.element.setParent(this))
        /* Elimina de los elementos del padre (space) el elemento que se está agrupado en composition (cluster) */
        this.getParent()?.getElements().forEach(parentElement => {
            if (_elements.some(element => element.element.getId() == parentElement.element.getId()))
                this.getParent().remove(parentElement.element)
        })
        return this;
    }

    getElement(element: Base | string): CompositionElement {
        return super.getElement(element) as CompositionElement
    }

    addElement(param: ComposableElement | Base, prevId?: string) {
        const group = super.addElement(param, prevId)
        const elements = group.getElements()
        const parent = group.getParent() as Space
        const isVirtual = group.isVirtual()
        const attributes = group.getAttributes()
        const dataComposition = { rules: group?.data?.rules }
        const result = CompositionFactory.getComposition(elements, parent, isVirtual, attributes, undefined, dataComposition)
        return result
    }

    getCompositions(): Composition[] {
        return [this]
    }

    setAttribute(attribute: baseAttribute, value: any, own?: any) {
        if (Toggles.isEnabled("RemoteDriverCP")) {
            if (attribute === "installation") {
                if (value.charAt(0) === remoteInstallation && this.allLightsHaveNoDriverOption()) {
                    const res = super.setAttribute(attribute, value, own)
                    this.getLights().forEach(light => light.element.setAttribute("control", "M No-driver"))
                    return res
                }
                if (value.charAt(0) !== remoteInstallation && this.allLightsHaveNoDriver()) {
                    const currentCompositionControl = this.getAttribute("control", true)
                    this.getLights().forEach(({ element }) => {
                        if (currentCompositionControl) {
                            element.setAttribute("control", currentCompositionControl)
                        } else {
                            element.deleteAttribute("control")
                        }
                    })
                }
            }
            if (attribute === "control" && this.allChildInstallationsRemote() && this.allLightsHaveNoDriver()) {
                return super.setAttribute(attribute, value, true)
            }
        }

        return super.setAttribute(attribute, value, own)
    }

    getAttributeVariation(attribute: any) {
        const modifier = this.getAttributeModifier(attribute)
        if (!modifier) return null
        const firstLight: Light = this.getLights()?.[0]?.element as Light
        if (!firstLight) return null
        const variation = firstLight.getAttributeVariation(attribute, modifier)
        return variation
    }

    allChildInstallationsRemote(): boolean {
        const lights = this.getLights()
        if (!lights.length) {
            return false
        }
        const lightsInstallations = lights.map(light => light.element.getAttributeModifier("installation"))
        const areAllInstallationsRemote = lightsInstallations.every(installation => installation?.charAt(0) === remoteInstallation)
        return areAllInstallationsRemote
    }

    allLightsHaveNoDriver(): boolean {
        const lights = this.getLights()
        const lightsControl = lights.map(light => light.element.getAttributeModifier("control"))
        const areAllControlsNoDriver = lightsControl.every(control => control === "M")
        return areAllControlsNoDriver
    }

    allLightsHaveNoDriverOption(): boolean {
        const lights = this.getLights()
        const someLightDoesNotHaveOption = lights.some(light => !(light.element as Light).getAttributeVariation("control", "M"))
        return !someLightDoesNotHaveOption
    }

    isCreatedFrom(selected: ComposableElement[]) {
        /* selected equals to all composition lights */
        return selected?.length == this?.getLights()?.length
            && selected.every((elem) => this?.getId() == elem?.element?.getParentByName('Composition')?.getId()) // FIXME: COMPOSITION
    }

    getBoundingBox(): { x: number; y: number; z: number; width: number; height: number; depth: number } {
        const vg = new Group(this.getLights(), null, true);
        return vg.getBoundingBox();
    }

    getOrigin() { // getBoundingBox of group needs to know if has Light, bc if not getOrigin from Base returing 'center' and getting y = 0
        return this.getLights()[0]?.element?.getOrigin()
    }

    moveElement(element: Base, x, y, z): any {
        // (this.parent as Base).moveElement(this, x, y, z)
        /* FIXME -> Se está asumiento que Composition siempre tiene un Space como padre */
        return (this.getParent() as Space).moveElement(element, x, y, z, true)
    }

    rotate2DElement(element: Base, rotationCenterX: any, rotationCenterZ: any, angle: any): any {
        /* FIXME -> Se está asumiento que Composition siempre tiene un Space como padre */
        return (this.getParent() as Space).rotate2DElement(element, rotationCenterX, rotationCenterZ, angle, true)
    }

    getInfo() {
        return {
            ...super.getInfo(),
            type: 'composition'
        }
    }

    isRemote() {
        return (this.allChildInstallationsRemote() && this.allLightsHaveNoDriver())
    }

    getDriver() {
        const isRemote = this.isRemote()
        if (!isRemote) return []
        const familyModel = this.getLights()[0].element.getModel()
        const compositionControl = this.getAttributeModifier("control")
        const articlesCodeAndControl = this.getLights().map(function (element, index, array) {
            // let articleCode = element.element.getModel();  This should be uncommented once Business requires to group different models in the same family
            let articleControlCode = element.element.getAttributeModifier("control");
            return { articleCode: familyModel, articleControlCode: compositionControl };
        });

        const articlesCodeAndControlAndCount = articlesCodeAndControl.reduce((acc, actual) => {
            let index = acc.findIndex((element) => element.articleCode == actual.articleCode && element.articleControlCode == actual.articleControlCode);

            if (index != -1) {
                acc[index].count += 1;

                return [...acc]
            } else {
                return [
                    ...acc,
                    { "articleCode": actual.articleCode, "articleControlCode": actual.articleControlCode, "count": 1 }
                ]
            }
        }, []);

        return Drivers.getTotalDrivers(articlesCodeAndControlAndCount);
    }

    fromJSON(json, options?: any, instance?: Base) {
        return Composition.fromJSON(json, options, instance)
    }

    static fromJSON(json, options?: any, instance?: Base) {
        const group = Group.fromJSON(json, { ...options, domainFactory: options?.domainFactory ?? new DomainFactory }, null)
        return super.fromJSON(json, options ?? { domainFactory: new DomainFactory }, instance ?? new Composition(group, null, null))
    }
}

export default Composition